import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    HostListener,
    OnDestroy,
    Inject,
    ViewChild,
    ElementRef,
    NgZone,
    AfterViewInit,
    ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    SalesDocumentType,
    SalesDocument,
    SalesDocumentPosition,
    State,
    scrollTop,
    DocumentStatus,
    PersonsService,
    SalesDocumentFilters,
    STATUS_FILTER_ALL,
    ColumnSortOrder,
    ColumnKey,
    documentListColumns,
    ClockService,
    APP_VERSION,
    DocumentWithChildren,
    Synced,
    SalesDocumentConnections,
    drawerAnimation,
} from '@icc/b2b/shared';
import { ConfigService, core, DynamicElementsService, ExchangeRateService } from '@icc/shared';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest, ReplaySubject, firstValueFrom } from 'rxjs';
import { filter, first, map, take, tap } from 'rxjs/operators';
import { getDocumentsConnections, getSalesDocumentsEntities, getSavedTagsInDb } from '../+state/sales-documents.selectors';
import { SalesDocumentService } from '../sales-document.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastService, parseDate } from '@icc/shared/helpers';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { OfferValidityService } from '../offer-validity/offer-validity.service';
import { SalesDocumentPersonsService } from '../sales-document-persons.service';
import { HttpClient } from '@angular/common/http';
import { SyncService } from '../sync.service';
import { LoaderService } from '../loader.service';
import { DocumentCreationService } from '../document-creation.service';
import { IDBServiceBrowser } from '../idb.service.browser';
import { CreateOrderDialogComponent } from '../create-order-dialog/create-order-dialog.component';
import { DialogService } from '@icc/ui';
import { validationDocument } from '../validators/document';
import { StatsService } from '@icc/shared/helpers/b2b';
import { BreakpointObserver } from '@angular/cdk/layout';
import { B2bDocumentManagerService } from '../b2b-document-manager.service';
import { startBuffering, stopBuffering } from '../+state/sales-documents.actions';
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service';
import { getDocumentPositions } from '../+state/sales-documents.selectors';
import { isDepartmentIncludedInOrderIntegration } from '../order-integration-departments';


@Component({
    selector: 'icc-sales-documents-list',
    templateUrl: './sales-documents-list.component.html',
    styleUrls: ['./sales-documents-list.component.scss'],
    animations: [drawerAnimation],
})
export class SalesDocumentsListComponent implements OnInit, OnDestroy, AfterViewInit {
    private deletedDocuments = new Set<string>();
    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    drawerAnimationState: 'fadeIn' | 'fadeOut' = 'fadeIn';
    @ViewChild('drawer', { static: false }) drawer: ElementRef;
    @ViewChild('drawerButton', { static: false, read: ElementRef }) drawerButton: ElementRef;

    localDocuments: SalesDocument[] = [];

    remoteDocuments: DocumentWithChildren[] = [];
    allDocuments: DocumentWithChildren[] = [];
    filteredDocuments: DocumentWithChildren[] = [];
    orderedDocuments: DocumentWithChildren[] = [];
    buyDocumentsMap: Record<string, string[]> = {};

    isOrderList = false;
    isServiceOrderList = false;
    isInquiryList = false;
    side: 'purchase' | 'sales' = 'sales';
    onlyUnplacedOrders = false;

    hasUnplacedOrders = false;

    workersList: Record<number, string> | null = null;
    documentStatuses: DocumentStatus[] = [];
    showDocumentStatuses = true;
    departments: Record<number, { name: string; priority: number }> = {};

    checkedDocuments: Record<string, boolean> = {};
    documentsIntegrity: Record<string, boolean> = {};
    showGroupOptions = false;
    hasAccessTo = {
        createQuotation: false,
        createServiceOrder: false,
        placeOrder: false,
        viewBuyPrices: false,
    };
    documentType: SalesDocumentType = SalesDocumentType.Quotation;
    synced = new Map<string, Synced>();

    documentActionsAccessBeforeSynced = new Map<
        string,
        {
            select: boolean;
            order: boolean;
            copy: boolean;
            transferChanges: boolean;
            delete: boolean;
            open: boolean;
        }
    >();
    documentActionsAccess = new Map<
        string,
        {
            select: boolean;
            order: boolean;
            copy: boolean;
            transferChanges: boolean;
            delete: boolean;
            open: boolean;
        }
    >();

    filters: SalesDocumentFilters;
    columnSortOptions: Partial<
        Record<
            ColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    columnsSortOrder: ColumnSortOrder = [];

    mode$ = this.route.data.pipe(map((data) => data.mode || 'index'));
    mode: 'selection' | 'index' = 'index';
    offline = false;

    @Input() documentsInPage = 20;
    totalItems = 0;

    private _page = 1;
    allPriceVersions: Map<number, { expiredDate: Date | null; effectiveDate: Date | null }>;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() pageEmitter = new EventEmitter();

    creating = false;

    subscriptions: Subscription[] = [];

    sortMethods: Record<ColumnKey, (a: DocumentWithChildren, b: DocumentWithChildren) => number> = {
        documentNumber: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const aNumber = (a.children?.[0] || a.doc).documentNumber?.join('').toUpperCase() || '';
            const bNumber = (b.children?.[0] || b.doc).documentNumber?.join('').toUpperCase() || '';
            if (aNumber > bNumber) {
                return 1;
            }
            if (bNumber > aNumber) {
                return -1;
            }
            return 0;
        },
        externalSource: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const aSource = (a.children?.[0] || a.doc).externalSource?.toLowerCase() ?? '';
            const bSource = (b.children?.[0] || b.doc).externalSource?.toLowerCase() ?? '';
            if (aSource > bSource) {
                return 1;
            }
            if (bSource > aSource) {
                return -1;
            }
            return 0;
        },
        client: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const clientA = (
                (a?.doc?.client?.companyName || '') +
                (a?.doc?.client?.firstName || '') +
                (a?.doc?.client?.lastName || '')
            ).toLowerCase();
            const clientB = (
                (b?.doc?.client?.companyName || '') +
                (b?.doc?.client?.firstName || '') +
                (b?.doc?.client?.lastName || '')
            ).toLowerCase();
            if (clientA > clientB) {
                return 1;
            }
            if (clientA < clientB) {
                return -1;
            }
            return 0;
        },
        seller: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const sellerA = (
                this.getSellerCompany(a.children?.[0] || a?.doc)?.name || ''
            ).toLowerCase();
            const sellerB = (
                this.getSellerCompany(b.children?.[0] || b?.doc)?.name || ''
            ).toLowerCase();
            if (sellerA > sellerB) {
                return 1;
            }
            if (sellerA < sellerB) {
                return -1;
            }
            return 0;
        },
        created: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const aDate = this.getOldestCreatedDate(a.children?.length ? a.children : [a.doc]);
            const bDate = this.getOldestCreatedDate(b.children?.length ? b.children : [b.doc]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        modified: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const aDate = this.getLatestModifiedDate(a.children?.length ? a.children : [a.doc]);
            const bDate = this.getLatestModifiedDate(b.children?.length ? b.children : [b.doc]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        requestedDeadline: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            const aDate = this.getOldestRequestedDeadline(a.children?.length ? a.children : [a.doc]);
            const bDate = this.getOldestRequestedDeadline(b.children?.length ? b.children : [b.doc]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        status: (a: DocumentWithChildren, b: DocumentWithChildren): number =>
            this.documentStatuses.findIndex((el) => el.id === +(a.children?.[0] || a.doc).statusId) -
            this.documentStatuses.findIndex((el) => el.id === +(b.children?.[0] || b.doc).statusId),
        priceBeforeDiscounts: (a: DocumentWithChildren, b: DocumentWithChildren): number =>
            (this.sum(a.children?.length ? a.children : [a.doc], 'priceBeforeDiscountsValue') ??
                0) -
            (this.sum(b.children?.length ? b.children : [b.doc], 'priceBeforeDiscountsValue') ?? 0),
        priceAfterDiscounts: (a: DocumentWithChildren, b: DocumentWithChildren): number =>
            (this.sum(a.children?.length ? a.children : [a.doc], 'priceAfterDiscountsValue') ?? 0) -
            (this.sum(b.children?.length ? b.children : [b.doc], 'priceAfterDiscountsValue') ?? 0),
        priority: (a: DocumentWithChildren, b: DocumentWithChildren): number => 0,
        primaryKey: (a: DocumentWithChildren, b: DocumentWithChildren): number => {
            if (a?.doc?.id > b?.doc?.id) {
                return 1;
            }
            if (a?.doc?.id < b?.doc?.id) {
                return -1;
            }
            return 0;
        },
        assignedToCompany: (a: DocumentWithChildren, b: DocumentWithChildren): number => 0,
        apsStatus: (a: DocumentWithChildren, b: DocumentWithChildren): number => 0,
        distributorStatus: (a: DocumentWithChildren, b: DocumentWithChildren): number => 0,
        manufacturerStatus: (a: DocumentWithChildren, b: DocumentWithChildren): number => 0,
    };

    onChangePage(page: number) {
        this.page = page;
        this.pageEmitter.emit(page);
        this.saveConfiguration();
        this.updateDocuments(true);
        scrollTop();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const width = event?.target?.innerWidth ? event.target.innerWidth : event;
        if (this.drawerAnimationState === 'fadeOut' && width > 1200) {
            this.drawerAnimationState = 'fadeIn';
        }
    }

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    localDocumentsResolve: () => void;
    loadedLocalDocuments: Promise<void>;

    mobileView = false;
    showDebugSection = false;

    deletedDocumentsSubscriptions = new Map<string, Subscription>();

    constructor(
        private store: Store<State>,
        private router: Router,
        private salesDocumentService: SalesDocumentService,
        private documentCreationService: DocumentCreationService,
        private priceVersionService: PriceVersionService,
        private configService: ConfigService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        private toastService: ToastService,
        private offerValidityService: OfferValidityService,
        private salesDocumentPersonsService: SalesDocumentPersonsService,
        private personsService: PersonsService,
        private http: HttpClient,
        private syncService: SyncService,
        private exchangeRateService: ExchangeRateService,
        @Inject(APP_VERSION) private appVersion: string,
        private loaderService: LoaderService,
        private idb: IDBServiceBrowser,
        private dialogService: DialogService,
        private dynamicElementsService: DynamicElementsService,
        private statsService: StatsService,
        private breakpointObserver: BreakpointObserver,
        private ngZone: NgZone,
        private documentManagerService: B2bDocumentManagerService,
        private changeDetectorRef: ChangeDetectorRef,
        private productsService: ProductsService
    ) {
        this.loadedLocalDocuments = new Promise((resolve) => {
            this.localDocumentsResolve = resolve;
        });
        this.subscriptions.push(
            this.priceVersionService.allPriceVersions$.subscribe((data) => {
                this.allPriceVersions = new Map();
                data?.forEach((item) => {
                    this.allPriceVersions.set(item?.id, {
                        expiredDate: item?.expiredDate || null,
                        effectiveDate: item?.effectiveDate || null,
                    });
                });
            }),
            combineLatest([
                this.route.data,
                this.route.queryParams,
            ]).subscribe(([data, queryParams]) => {
                this.documentType = data.documentType;
                this.isOrderList = data.documentType === SalesDocumentType.Order;
                this.isInquiryList = data.documentType === SalesDocumentType.Inquiry;
                this.isServiceOrderList = data.documentType === SalesDocumentType.ServiceOrder;
                this.side = data.purchase ? 'purchase' : 'sales';
                this.onlyUnplacedOrders = data.unplaced === true;
                this.documentStatuses =
                this.configService.user.statusSystems?.[data.documentType]?.statuses ?? [];
                this.showDocumentStatuses = this.configService.user.statusSystems?.[
                    data.documentType
                ]?.system?.showStatusesInB2b;
                this.setListCrumbs();
                if (queryParams.first) {
                    this.router.navigate([], {
                        queryParams: {
                            first: null,
                        },
                        queryParamsHandling: 'merge',
                    });
                    this.filters = null;
                    this.setColumnSortDefaults();
                } else {
                    this.loadConfiguration();
                }

                let statsKey = 'quotations';
                if (this.isOrderList) {
                    statsKey = 'purchase-orders';
                } else if (this.isInquiryList) {
                    statsKey = 'inquiries';
                }
                this.statsService.add({
                    action: `open-${statsKey}-list`,
                });
                this.updateDocuments(true);
            }),
            this.store.select(getDocumentsConnections).subscribe((connections) => {
                this.updateDocumentsPairs(connections);
            }),
            this.salesDocumentService.isSynced$().subscribe(({synced}) => {
                this.synced = synced;
                this.updateDocumentAccessAfterSync();
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.setWorkersList();
                this.setGeneralAccess();
                this.updateDocuments(true);
            }),
            this.salesDocumentService.deletedDocuments$.subscribe((deletedDocuments) => {
                this.deletedDocuments = new Set(deletedDocuments);
                deletedDocuments.forEach((documentId) => {
                    if (!this.deletedDocumentsSubscriptions.has(documentId)) {
                        this.updateDocumentAccessAfterDelete(documentId);
                        this.deletedDocumentsSubscriptions.set(documentId, this.store.select(getSalesDocumentsEntities).pipe(
                            filter((entities) => !entities[documentId]),
                            take(1),
                        ).subscribe(() => {
                            this.salesDocumentService.removeDeletedDocument(documentId);
                            if (this.deletedDocumentsSubscriptions.has(documentId)) {
                                this.deletedDocumentsSubscriptions.get(documentId).unsubscribe();
                                this.deletedDocumentsSubscriptions.delete(documentId);
                            }
                            this.updateDocuments(true);
                        }));
                    }
                });
            }),
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.mode$.subscribe((mode) => {
                this.mode = mode;
            }),
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe((result) => {
                this.mobileView = result.matches;
                if (result.matches && this.drawerAnimationState === 'fadeIn') {
                    this.drawerAnimationState = 'fadeOut';
                }
            })
        );
        this.setWorkersList();
        this.loadDepartments();
        this.setGeneralAccess();
        this.showDebugSection = (window as any).isDebugMode();
        window.addEventListener('debugModeChanged', this.updateDebugMode);
    }

    ngAfterViewInit(): void {
        let top = document.getElementById('top');
        if (top !== null) {
            top.scrollIntoView();
            top = null;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
        this.deletedDocumentsSubscriptions.forEach((s) => s.unsubscribe());
        window.removeEventListener('debugModeChanged', this.updateDebugMode);
    }

    createOffer() {
        if (!this.creating) {
            this.creating = true;
            firstValueFrom(this.priceVersionService.loaded$).then(() => {
                this.exchangeRateService.loaded.then(() => {
                    const newDocument = this.salesDocumentService.createAndSaveDocument();
                    this.salesDocumentService.openDocument(newDocument);
                    this.router.navigate(['trade', 'offer', newDocument.uuid], {
                        state: {
                            documentType: this.documentType,
                            side: this.side,
                            unplaced: this.onlyUnplacedOrders,
                        },
                    });
                });
            });
        }
    }

    createServiceOrder() {
        if (!this.creating) {
            this.creating = true;
            firstValueFrom(this.priceVersionService.loaded$).then(() => {
                this.exchangeRateService.loaded.then(() => {
                    const newDocument = this.salesDocumentService.createAndSaveServiceOrder();
                    this.salesDocumentService.openDocument(newDocument);
                    this.router.navigate(['trade', 'offer', newDocument.uuid], {
                        state: {
                            documentType: this.documentType,
                            side: this.side,
                            unplaced: this.onlyUnplacedOrders,
                        },
                    });
                });
            });
        }
    }

    changeDrawerAnimationState() {
        this.drawerAnimationState = 'fadeIn';
    }

    closeDrawer() {
        this.drawerAnimationState = 'fadeOut';
    }

    getDrawerBtnIconName(): 'more_horizontal' | 'more_vertical' {
        return this.drawerAnimationState === 'fadeIn' ? 'more_horizontal' : 'more_vertical';
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        if (this.drawerAnimationState === 'fadeIn' && this.mobileView) {
            const clickedInsideDrawer = this.drawer?.nativeElement && (
                targetElement && this.drawer?.nativeElement?.contains(targetElement) || this.drawerButton?.nativeElement?.contains(targetElement)
            );
            if (!clickedInsideDrawer) {
                this.ngZone.run(() => {
                    this.closeDrawer();
                });
            }
        }
    }

    open(document: SalesDocument) {
        this.salesDocumentService.openDocument(document);
        this.router.navigate(['trade', 'offer', document.uuid], {
            state: {
                documentType: this.documentType,
                side: this.side,
                unplaced: this.onlyUnplacedOrders,
            },
        });
    }

    async select(document: SalesDocument) {
        this.syncService.addCurrentDocumentId(document.uuid);
        await this.syncService.waitForSynced([document.uuid]);
        this.salesDocumentService.selectedDocument$.next(document);
    }

    async copy(document: SalesDocument, node: DocumentWithChildren) {
        this.loaderService.setLoading();
        const neededSet = new Set<string>([document.uuid]);
        await this.syncService.addCurrentDocumentId(document.uuid);
        for (const child of node.children) {
            neededSet.add(child.uuid);
            await this.syncService.addCurrentDocumentId(child.uuid);
        }
        for (const connectedId of node.connectedDocumentIds) {
            neededSet.add(connectedId);
            await this.syncService.addCurrentDocumentId(connectedId);
        }
        await this.syncService.waitForSynced(Array.from(neededSet));
        await this.syncService.loadDocuments(Array.from(neededSet));

        // Check for inactive products before copying
        const hasInactiveProducts = await this.checkForInactiveProducts(document.uuid);
        if (hasInactiveProducts) {
            this.loaderService.setLoaded();
            this.toastService.add({
                title: this.translateService.instant('DOCUMENT|Kopiowanie dokumentu'),
                body: this.translateService.instant(
                    'DOCUMENT|Nie można skopiować dokumentu zawierającego produkty zablokowane do kopiowania.'
                ),
                severity: 'warn',
            });
            return;
        }

        this.store.dispatch(startBuffering());
        const {
            sales: newDocument,
        } = await this.documentCreationService.duplicateSalesAndBuyDocuments(document);
        const tagId = core.generateUUID();
        this.documentManagerService.tagChanges(newDocument.uuid, {
            type: 'documentCopied',
            uuid: tagId,
        });
        this.store.dispatch(stopBuffering());
        await this.store.select(getSavedTagsInDb).pipe(
            first(savedOps => savedOps.has(tagId))
        ).toPromise();

        this.router.navigate(['trade', 'offer', newDocument.uuid], {
            state: {
                documentType: this.documentType,
                side: this.side,
                unplaced: this.onlyUnplacedOrders,
            },
        });
    }

    private async checkForInactiveProducts(documentId: string): Promise<boolean> {
        const positions = await this.store.select(getDocumentPositions(documentId)).pipe(first()).toPromise() as SalesDocumentPosition[];
        if (!positions?.length) {
            return false;
        }

        const productIds = positions
            .filter(p => p.productId)
            .map(p => p.productId);

        if (productIds.length === 0) {
            return false;
        }

        const allProducts = await this.productsService.getData().then(data => data.productsList);
        const productMap = new Map(allProducts.map(p => [p.id, p]));

        for (const position of positions) {
            if (position.productId && position.type !== 'additional') {
                const product = productMap.get(position.productId);
                if (!product || !product.isActive) {
                    return true;
                }
            }
        }
        return false;
    }

    private canBeEdited(document: SalesDocument) {
        return !document.isConfirmed && !document.isOrderPlaced && !document.isCanceled;
    }

    async getLocalDocuments() {
        this.loaderService.setLoadingAfterTime();
        const allDocuments = await this.idb.getAllDb('docs');
        this.localDocuments = allDocuments.filter(el => el.type === 'sales' && !el.isDeleted) as SalesDocument[];
        this.loaderService.setLoaded();
    }

    async getRemoteDocuments() {
        this.loaderService.setLoadingAfterTime();
        let serverDocuments;
        try {
            serverDocuments = await this.http.post<{
                documents: DocumentWithChildren[],
                limit: number,
                offset: number,
                totalCount: number,
            }>(`${window.location.origin}/api/b2b/documents`, {
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
                salesDocumentTypeId: this.documentType,
                side: this.side,
                unplaced: this.onlyUnplacedOrders,
                filters: this.filters || {},
                sort: this.columnsSortOrder || [],
                offset: (this.page - 1) * this.documentsInPage,
                limit: this.documentsInPage,
                type: 'sales',
            }).toPromise();
        } catch (error) {
            this.toastService.add({
                title: this.translateService.instant('DOCUMENT|Błąd pobierania dokumentów'),
                body: this.translateService.instant('DOCUMENT|Brak uprawnień lub sesja wygasła.'),
                severity: 'error',
            });
        }
        this.remoteDocuments = serverDocuments.documents.map(doc => {
            doc.doc.created = parseDate(doc.doc.created);
            doc.doc.modified = parseDate(doc.doc.modified);
            if (doc.doc.rootCreated) {
                doc.doc.rootCreated = parseDate(doc.doc.rootCreated);
            }
            if (doc.doc.validityDate) {
                doc.doc.validityDate = parseDate(doc.doc.validityDate);
            }
            if (doc.doc.deletedAt) {
                doc.doc.deletedAt = parseDate(doc.doc.deletedAt);
            }
            if (doc.doc.declaredDeadline) {
                doc.doc.declaredDeadline = parseDate(doc.doc.declaredDeadline);
            }
            if (doc.doc.requestedDeadline) {
                doc.doc.requestedDeadline = parseDate(doc.doc.requestedDeadline);
            }
            doc.children.forEach(child => {
                child.created = parseDate(child.created);
                child.modified = parseDate(child.modified);
                if (child.rootCreated) {
                    child.rootCreated = parseDate(child.rootCreated);
                }
                if (child.validityDate) {
                    child.validityDate = parseDate(child.validityDate);
                }
                if (child.deletedAt) {
                    child.deletedAt = parseDate(child.deletedAt);
                }
                if (child.declaredDeadline) {
                    child.declaredDeadline = parseDate(child.declaredDeadline);
                }
                if (child.requestedDeadline) {
                    child.requestedDeadline = parseDate(child.requestedDeadline);
                }
            });
            return doc;
        });
        this.totalItems = serverDocuments.totalCount;
        this.loaderService.setLoaded();
    }

    async updateDocuments(updateRemote = false) {
        if (this.offline) {
            await this.getLocalDocuments();
            const allDocuments = await this.filterDocumentsByType(this.localDocuments);
            this.allDocuments = await this.prepareDocumentLists(allDocuments);
            this.filteredDocuments = this.filterDocuments();
            this.orderedDocuments = this.sort();
            this.totalItems = this.filteredDocuments.length;
        } else {
            if (updateRemote) {
                await this.getRemoteDocuments();
                for (const doc of this.remoteDocuments) {
                    if (doc?.doc?.uuid) {
                        this.buyDocumentsMap[doc.doc.uuid] = doc.children.map(child => child.uuid).filter((uuid, index, self) => self.indexOf(uuid) === index);
                    }
                }
            }
            this.filteredDocuments = this.remoteDocuments;
            this.orderedDocuments = this.remoteDocuments;
        }
        this.updateHasUnplacedOrders();
        await this.updateIntegrity();
        await this.setDocumentsAccess();
        await this.updateDocumentAccessAfterSync();
        this.changeDetectorRef.detectChanges();
    }

    async filterDocumentsByType(documents: SalesDocument[]): Promise<SalesDocument[]> {
        const matchingDocuments: SalesDocument[] = [];
        for (const doc of documents) {
            switch (this.documentType) {
                case SalesDocumentType.Quotation:
                    if (
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        doc.salesDocumentTypeId === this.documentType &&
                        (await this.salesDocumentService.hasAccessToQuotationOrInquiry(doc)) &&
                        (this.mode !== 'selection' || this.canBeEdited(doc))
                    ) {
                        matchingDocuments.push(doc);
                    }
                    break;
                case SalesDocumentType.Order:
                    if (
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToOrder(doc, this.onlyUnplacedOrders ? 'manage' : 'view'))
                    ) {
                        matchingDocuments.push(doc);
                    } else if (
                        this.isOrderList &&
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.buyerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToOrder(doc, this.onlyUnplacedOrders ? 'manage' : 'view')) &&
                        (!this.onlyUnplacedOrders ||
                            (!doc.isOrderPlaced &&
                                doc.isConfirmed &&
                                !doc.isCanceled))
                    ) {
                        matchingDocuments.push(doc);
                    }
                    break;
                case SalesDocumentType.Inquiry:
                    if (
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToQuotationOrInquiry(doc))
                    ) {
                        matchingDocuments.push(doc);
                    } else if (
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.buyerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToQuotationOrInquiry(doc))
                    ) {
                        matchingDocuments.push(doc);
                    }
                    break;
                case SalesDocumentType.ServiceOrder:
                    if (
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToServiceOrder(doc)) &&
                        (doc.isOrderPlaced || doc.authorCompanyId === Number(this.configService?.user?.user?.ActiveCompany))
                    ) {
                        matchingDocuments.push(doc);
                    } else if (
                        doc.salesDocumentTypeId === this.documentType &&
                        Number(doc.buyerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        (await this.salesDocumentService.hasAccessToServiceOrder(doc)) &&
                        (doc.isOrderPlaced || doc.authorCompanyId === Number(this.configService?.user?.user?.ActiveCompany))
                    ) {
                        matchingDocuments.push(doc);
                    }
                    break;
            }
        }
        return matchingDocuments;
    }

    async prepareDocumentLists(documents: SalesDocument[]): Promise<DocumentWithChildren[]> {
        let matchingDocuments: DocumentWithChildren[] = [];
        const buyDocumentsMap: Record<string, DocumentWithChildren> = {};
        for (const doc of documents) {
            switch (this.documentType) {
                case SalesDocumentType.Quotation:
                    if (
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany)
                    ) {
                        matchingDocuments.push({
                            doc,
                            children: [],
                            connectedDocumentIds: [],
                            connectedDocumentsStatuses: [],
                        });
                    }
                    break;
                case SalesDocumentType.Order:
                    if (
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany)
                    ) {
                        const node: DocumentWithChildren = {
                            doc,
                            children: [],
                            connectedDocumentIds: [],
                            connectedDocumentsStatuses: [],
                        };
                        buyDocumentsMap[doc.uuid] = node;
                        matchingDocuments.push(node);
                    }
                    break;
                case SalesDocumentType.Inquiry:
                    if (
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany)
                    ) {
                        const node: DocumentWithChildren = {
                            doc,
                            children: [],
                            connectedDocumentIds: [],
                            connectedDocumentsStatuses: [],
                        };
                        buyDocumentsMap[doc.uuid] = node;
                        matchingDocuments.push(node);
                    }
                    break;
                case SalesDocumentType.ServiceOrder:
                    if (
                        Number(doc.sellerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) ||
                        Number(doc.buyerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany)
                    ) {
                        const node: DocumentWithChildren = {
                            doc,
                            children: [],
                            connectedDocumentIds: [],
                            connectedDocumentsStatuses: [],
                        };
                        buyDocumentsMap[doc.uuid] = node;
                        matchingDocuments.push(node);
                    }
                    break;
            }
        }
        if (this.side === 'purchase') {
            for (const doc of documents) {
                if (
                    Number(doc.buyerCompanyId) ===
                        Number(this.configService?.user?.user?.ActiveCompany)
                ) {
                    const salesDocument = await this.salesDocumentService.getSalesDocumentForBuyDocument(
                        doc.uuid
                    );
                    if (salesDocument?.uuid && buyDocumentsMap[salesDocument?.uuid].children) {
                        buyDocumentsMap[salesDocument?.uuid].children.push(doc);
                    } else if (!salesDocument?.uuid && !buyDocumentsMap[doc.uuid]) {
                        buyDocumentsMap[doc.uuid] = {
                            doc,
                            children: [],
                            onlyPurchase: true,
                            connectedDocumentIds: [],
                            connectedDocumentsStatuses: [],
                        };
                        matchingDocuments.push(buyDocumentsMap[doc.uuid]);
                    }
                }
            }
        }
        if (this.side === 'purchase') {
            matchingDocuments = matchingDocuments.filter(
                (el) => el.children?.length > 0 || el.onlyPurchase
            );
        }

        // Populate connectedDocumentsStatuses for offline documents
        await this.populateConnectedDocumentsStatuses(matchingDocuments, documents);

        return matchingDocuments;
    }

    /* Filters */

    setFilters({ filters, reset }: { filters: SalesDocumentFilters; reset: boolean }) {
        this.filters = filters;
        if (reset) {
            this.setColumnSortDefaults();
        }
        this.page = 1;
        this.updateDocuments(true);
        this.saveConfiguration();
        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    filterDocuments(): DocumentWithChildren[] {
        let documents = [...this.allDocuments];

        if (this.filters) {
            if (this.filters && documents && documents.length) {
                if (this.filters.nrOffer) {
                    documents = this.filterByNrOfferAndNrOrder(documents, this.filters.nrOffer);
                } else if (this.filters.nrOrder) {
                    documents = this.filterByNrOfferAndNrOrder(documents, this.filters.nrOrder);
                }
                if (this.filters?.documentCode)
                    documents = this.filterByDocumentCode(documents, this.filters.documentCode);

                if (this.filters?.status === STATUS_FILTER_ALL) {
                    // STATUS_FILTER_ALL: pokazuj wszystkie dokumenty, w tym zamknięte (aneksowane, anulowane itp.)
                } else if (this.filters?.status) {
                    documents = this.filterByStatus(documents);
                } else {
                    documents = this.filterOutClosedStatuses(documents);
                }

                if (this.filters?.creationDateFrom)
                    documents = this.filterByCreationDateFrom(documents);

                if (this.filters?.creationDateTo)
                    documents = this.filterByCreationDateTo(documents);

                if (this.filters?.validityDateFrom)
                    documents = this.filterByValidityDateFrom(documents);

                if (this.filters?.validityDateTo)
                    documents = this.filterByValidityDateTo(documents);

                if (this.filters?.client) documents = this.filterByClient(documents);

                if (this.filters?.authorUserId) documents = this.filterByAutorUserId(documents);
            }
        }
        return documents;
    }

    setSearchableDocumentNumber(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        for (const document of documents) {
            document.doc.searchableDocumentNumber = document.doc.documentNumber.join('');
        }
        return documents;
    }

    filterByNrOfferAndNrOrder(
        documents: DocumentWithChildren[],
        searchWord: string
    ): DocumentWithChildren[] {
        documents = this.setSearchableDocumentNumber(documents);
        if (documents) {
            return documents.filter(
                (document) =>
                    document.doc.searchableDocumentNumber?.toLowerCase().includes(searchWord.toLowerCase()) ||
                    document.doc.documentName?.toLowerCase().includes(searchWord.toLowerCase())
            );
        }
        return [];
    }

    filterByDocumentCode(
        documents: DocumentWithChildren[],
        documentCode: string
    ): DocumentWithChildren[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.doc.documentCode?.toLowerCase().includes(documentCode.toLowerCase())
            );
        }
        return [];
    }

    filterByStatus(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        return documents.filter((document) => +document.doc.statusId === +this.filters.status);
    }

    filterOutClosedStatuses(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        return documents.filter(
            (document) => !document.doc.isAnnexed && !document.doc.isCanceled
        );
    }

    filterByCreationDateFrom(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        const date = parseDate(this.filters.creationDateFrom);
        if (date) {
            return documents.filter((document) =>
                document.doc.created ? new Date(document.doc.created) >= date : true
            );
        } else {
            return [];
        }
    }

    filterByCreationDateTo(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        const date = parseDate(this.filters.creationDateTo);
        if (date) {
            return documents.filter((document) =>
                document.doc.created
                    ? new Date(document.doc.created) <= new Date(date.setHours(23, 59, 59, 999))
                    : true
            );
        } else {
            return [];
        }
    }

    filterByValidityDateFrom(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        const date = parseDate(this.filters.validityDateFrom);
        if (date) {
            return documents.filter((document) => {
                let validityDate = document.doc.validityDate;
                const documentPriceVersion = this.allPriceVersions?.get(
                    document?.doc.priceVersionId
                );
                if (documentPriceVersion) {
                    validityDate = OfferValidityService.calculateValidity(
                        documentPriceVersion,
                        document.doc.validityDate,
                        this.configService?.user?.offerDatePriority,
                        document?.doc.isConfirmed,
                        document?.doc.hasOnlyCustomValuatedPositions,
                        document?.doc.ignorePriceVersionValidity,
                    );
                }

                return validityDate ? new Date(validityDate) >= new Date(date) : true;
            });
        } else {
            return [];
        }
    }

    filterByValidityDateTo(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        const date = parseDate(this.filters.validityDateTo);
        if (date) {
            return documents?.filter((document) => {
                let validityDate = document.doc.validityDate;
                const documentPriceVersion = this.allPriceVersions?.get(
                    document?.doc.priceVersionId
                );
                if (documentPriceVersion) {
                    validityDate = OfferValidityService.calculateValidity(
                        documentPriceVersion,
                        document.doc.validityDate,
                        this.configService?.user?.offerDatePriority,
                        document?.doc.isConfirmed,
                        document?.doc.hasOnlyCustomValuatedPositions,
                        document?.doc.ignorePriceVersionValidity,
                    );
                }
                return validityDate
                    ? new Date(validityDate).setHours(0) <= new Date(date).setHours(23, 59, 59, 999)
                    : true;
            });
        } else {
            return [];
        }
    }

    filterByClient(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.doc.client?.firstName?.toLowerCase().includes(this.filters.client.toLowerCase()) ||
                    document.doc.client?.lastName?.toLowerCase().includes(this.filters.client.toLowerCase()) ||
                    document.doc.client?.companyName?.toLowerCase().includes(this.filters.client.toLowerCase())
            );
        }
        return [];
    }

    filterByAutorUserId(documents: DocumentWithChildren[]): DocumentWithChildren[] {
        return documents.filter(
            (document) => +document.doc.authorUserId === +this.filters.authorUserId
        );
    }

    private setColumnSortDefaults() {
        if (this.isOrderList) {
            this.columnsSortOrder = core.copy(
                this.configService.user.documentListDefaultOrder.order
            );
        } else if (this.isServiceOrderList) {
            this.columnsSortOrder = core.copy(
                this.configService.user.documentListDefaultOrder.serviceOrder
            );
        } else {
            this.columnsSortOrder = core.copy(
                this.configService.user.documentListDefaultOrder.quotation
            );
        }
        this.setColumnSortOptions();
        this.saveConfiguration();
    }

    private setColumnSortOptions() {
        documentListColumns.forEach((el) => {
            this.columnSortOptions[el] = null;
        });

        this.columnsSortOrder.forEach((el, i) => {
            if (el.column) {
                this.columnSortOptions[el.column] = {
                    index: i + 1,
                    order: el.order,
                };
            }
        });
    }

    sort(): DocumentWithChildren[] {
        let documents = [...this.filteredDocuments];

        if (this.columnsSortOrder) {
            if (this.columnsSortOrder && documents && documents.length) {
                documents = documents.sort((a, b) => {
                    let i = -1;
                    let order = 0;
                    while (order === 0 && this.columnsSortOrder[i + 1]) {
                        i++;
                        if (
                            typeof this.sortMethods[this.columnsSortOrder[i].column] === 'function'
                        ) {
                            order = this.sortMethods[this.columnsSortOrder[i].column](a, b);
                            if (this.columnsSortOrder[i].order === 'desc') {
                                if (order < 0) {
                                    order = 1;
                                } else if (order > 0) {
                                    order = -1;
                                }
                            }
                        }
                    }

                    if (order === 0) {
                        order = this.sortMethods['created'](a, b);
                    }

                    return order;
                });
            }
        }
        return documents;
        // return documents.filter((doc) => doc.doc.isOrderPlaced);
    }

    orderBy(column: ColumnKey) {
        const columnOrderIndex = this.columnsSortOrder.findIndex((el) => el.column === column);
        let columnOrder = null;
        if (columnOrderIndex > -1) {
            columnOrder = this.columnsSortOrder[columnOrderIndex];
            this.columnsSortOrder.splice(columnOrderIndex, 1);
        }
        this.columnsSortOrder.unshift({
            column,
            order: columnOrder?.order === 'asc' ? 'desc' : 'asc',
        });
        if (this.columnsSortOrder.length > 3) {
            this.columnsSortOrder.splice(3, this.columnsSortOrder.length - 3);
        }

        this.setColumnSortOptions();
        this.page = 1;
        this.updateDocuments(true);
        this.saveConfiguration();
    }

    saveConfiguration() {
        let key = 'offers';
        if (this.isOrderList) {
            if (this.side === 'purchase') {
                if (this.onlyUnplacedOrders) {
                    key = 'purchase-orders-unplaced';
                } else {
                    key = 'purchase-orders';
                }
            } else {
                key = 'orders';
            }
        } else if (this.isInquiryList) {
            key = 'inquiries';
        }
        sessionStorage.setItem(
            `sales-documents-list-${key}-${
                this.configService?.user?.user?.id
            }`,
            core.stringJson({
                sort: this.columnsSortOrder,
                filters: this.filters,
                page: this.page,
                offline: this.offline,
            })
        );
    }

    loadConfiguration() {
        let key = 'offers';
        if (this.isOrderList) {
            if (this.side === 'purchase') {
                if (this.onlyUnplacedOrders) {
                    key = 'purchase-orders-unplaced';
                } else {
                    key = 'purchase-orders';
                }
            } else {
                key = 'orders';
            }
        } else if (this.isInquiryList) {
            key = 'inquiries';
        }
        const { sort, filters, page, offline } = core.parseJson(
            sessionStorage.getItem(
                `sales-documents-list-${key}-${
                    this.configService?.user?.user?.id
                }`
            )
        );
        this.page = page ?? 1;

        if (typeof filters === 'object') {
            this.filters = filters;
        } else {
            this.filters = null;
        }

        if (Array.isArray(sort)) {
            this.columnsSortOrder = sort;
            this.setColumnSortOptions();
        } else {
            this.setColumnSortDefaults();
        }

        if (typeof offline === 'boolean') {
            this.offline = offline;
        }
    }

    async setWorkersList() {
        if (
            !this.workersList &&
            (await this.salesDocumentService.hasPermissionSellAllQuotationsList())
        ) {
            this.workersList = await this.salesDocumentService.getWorkersList();
        }
    }

    async loadDepartments() {
        await this.personsService.loaded;
        this.departments = this.personsService.getDepartments();
    }

    getStatus(document: SalesDocument) {
        return this.documentStatuses.find((el) => Number(el.id) === Number(document?.statusId));
    }

    hasUnplacedConnectedDocuments(node: DocumentWithChildren): boolean {
        if (!node.connectedDocumentsStatuses || !Array.isArray(node.connectedDocumentsStatuses)) {
            return false;
        }

        return node.connectedDocumentsStatuses.some(statusId => {
            const status = this.documentStatuses.find(s => s.id === statusId);
            return status?.code === 'ORDER_NOT_PLACED';
        });
    }

    updateHasUnplacedOrders() {
        this.hasUnplacedOrders = this.filteredDocuments.some(
            (doc) =>
                doc.children.length > 0 &&
                doc.children.some(
                    (child) =>
                        child.salesDocumentTypeId === SalesDocumentType.Order &&
                        Number(child.buyerCompanyId) ===
                            Number(this.configService?.user?.user?.ActiveCompany) &&
                        !child.isOrderPlaced &&
                        child.isConfirmed
                )
        );
    }

    async updateIntegrity() {
        const offset = (this.page - 1) * this.documentsInPage;
        const limit = this.documentsInPage;
        const documents = (this.offline
            ? this.orderedDocuments.slice(offset, offset + limit)
            : this.orderedDocuments
        );
        const documentIds = documents.map((doc) => doc.doc.uuid);
        const buyDocuments = await this.documentManagerService.getBuyDocumentsForAllDocuments(documentIds);
        for (const document of documents) {
            this.documentsIntegrity[
                document.doc.uuid
            ] = await this.documentManagerService.checkIntegrity(document.doc, buyDocuments.get(document.doc.uuid));
        }
    }

    onChecked() {
        const checkedDocuments = Object.entries(this.checkedDocuments)
            .filter(([, checked]) => checked)
            .map(([uuid]) => uuid);
        this.showGroupOptions = checkedDocuments.length > 0;
    }

    private isOrderIntegrationActive(document: SalesDocument, buyDocuments?: SalesDocument[]): boolean {
        if (this.configService.user.orderIntegration !== 'OrdersSynchronization') {
            return false;
        }
        const departments = this.configService.user.orderIntegrationDepartments;
        if (!departments?.length) return true;
        const docsToCheck = buyDocuments?.length > 0 ? buyDocuments : [document];
        return docsToCheck.some((doc) =>
            isDepartmentIncludedInOrderIntegration(departments, doc.sellerProductDepartmentId)
        );
    }
    async confirmPlaceOrder(document: DocumentWithChildren) {
        if (!this.isOrderIntegrationActive(document.doc, document.children) &&
            document.children[0].salesDocumentTypeId === SalesDocumentType.Order &&
            document.children[0].isOrderCreated &&
            !document.children[0].isOrderPlaced
        ) {
            return this.placeOrder(document);
        }

        if (!this.configService.user.blockedOrdering) {
            await this.dialogService
                .open(CreateOrderDialogComponent, {
                    data: {
                        document: document.doc,
                        buyDocuments: document.children,
                        documentIdToSendEmail: document.doc.uuid,
                        order: (orderLater, deliveryDate) => this.order(document, false, deliveryDate),
                        placeOrder: (orders, deliveryDate, documentIdToSendEmailLocal) => this.placeOrder(document, deliveryDate),
                        withdrawPlaceOrders: () => this.withdrawPlaceOrders(document),
                        onlyPlace: true,
                    },
                })
                .afterClosed()
                .toPromise();
        } else {
            this.toastService.add({
                title: this.translateService.instant('SALES_DOCUMENT|Zamawianie zablokowane'),
                body: this.dynamicElementsService.getElementContentBySlug('blocked-ordering-message', true),
                severity: 'warn',
            });
        }
    }

    async withdrawPlaceOrders(document: DocumentWithChildren) {
        const orderedDocuments: SalesDocument[] = [];
        for (const child of document.children) {
            const { invalidOffer: invalidChild } = this.offerValidityService.getValidDate(child);
            const canManageChildren = await this.salesDocumentService.hasAccessToOrder(
                child,
                'manage'
            );
            const canOrderChildren =
                canManageChildren &&
                child.isConfirmed &&
                !child.isCanceled &&
                !child.isOrderPlaced &&
                !child.isAnnexed &&
                !invalidChild &&
                (await this.permissionsService.hasPermission(['purchase-make-order'])) &&
                child.externalSource !== 'ERP';
            if (canOrderChildren) {
                orderedDocuments.push(child);
            }
        }
        if (orderedDocuments?.length > 0) {
            await this.salesDocumentService.withdrawPlaceOrders(orderedDocuments);
            return {
                success: true,
                orders: orderedDocuments,
            };
        }
    }

    async order(document: DocumentWithChildren, orderLater = false, deliveryDate: string = null) {
        if (!document?.doc?.isOrderPlaced) {
            let validDocuments = true;
            const processedDocuments = document.children?.length ? document.children : [document.doc];
            for (const buyDocument of processedDocuments) {
                validDocuments =
                    validDocuments && !(await this.documentHasErrors(buyDocument, true, document));
            }
            if (validDocuments) {
                const newDocuments = await this.salesDocumentService.createOrder(document.doc, orderLater, deliveryDate);
                const orders =
                    !document.children?.length ? [newDocuments.sales] : newDocuments.buy;
                if (orderLater) {
                    if (this.configService.config.config.Trade.orderEmails) {
                        this.salesDocumentService.prepareOrderEmail(orders, newDocuments.sales.uuid, orderLater);
                    }
                    return {
                        success: true,
                        orders,
                        documentIdToSendEmail: newDocuments.sales.uuid,
                    };
                } else {
                    const placeResults = await this.salesDocumentService.placeOrders(orders, deliveryDate, newDocuments.sales.uuid);
                    return this.salesDocumentService.afterOrderPlaced(placeResults, orders, newDocuments.sales.uuid);
                }
            } else {
                return {
                    success: true,
                    orders: [],
                    documentIdToSendEmail: null,
                };
            }
        }
    }

    private async documentHasErrors(
        document: SalesDocument,
        setDocumentErrorsMessage = false,
        documentWithChildren: DocumentWithChildren = null
    ): Promise<boolean> {
        const documentErrors = validationDocument(document, this.translateService);
        if (
            documentErrors.errors.length !== 0 ||
            documentErrors.client.errors.length !== 0 ||
            documentErrors.client.contact.errors.length !== 0 ||
            documentErrors.client.invoice.errors.length !== 0
        ) {
            if (setDocumentErrorsMessage) {
                documentWithChildren.message = this.translateService.instant('SALES_DOCUMENT|Błąd w danych');
                documentWithChildren.messageState = 'error'
            }
            return true;
        } else {
            return false;
        }
    }

    async placeOrder(document: DocumentWithChildren, deliveryDate: string = null) {
        document.orderPlaced = false;
        document.message = this.translateService.instant('SALES_DOCUMENT|Trwa zamawianie...');
        document.messageState = 'default';
        const orderedDocuments: SalesDocument[] = [];
        for (const child of document.children) {
            const { invalidOffer: invalidChild } = this.offerValidityService.getValidDate(child);
            const canManageChildren = await this.salesDocumentService.hasAccessToOrder(
                child,
                'manage'
            );
            const canOrderChildren =
                canManageChildren &&
                child.isConfirmed &&
                !child.isCanceled &&
                !child.isOrderPlaced &&
                !child.isAnnexed &&
                !invalidChild &&
                (await this.permissionsService.hasPermission(['purchase-make-order'])) &&
                child.externalSource !== 'ERP';
            if (canOrderChildren) {
                orderedDocuments.push(child);
            }
        }
        const placedResult = await this.salesDocumentService.placeOrders(orderedDocuments, deliveryDate, document.doc.uuid);
        const result = await this.salesDocumentService.afterOrderPlaced(placedResult, orderedDocuments, document.doc.uuid);
        document.orderPlaced = result.success;
        if (!result.success) {
            if (result.messages?.length) {
                document.message = result.messages.join(' ');
            } else {
                document.message = this.translateService.instant('SALES_DOCUMENT|Składanie zamówienia nie powiodło się.');
            }
            document.messageState = 'error';
        } else {
            document.message = '';
            document.messageState = 'default';
            this.updateDocuments(true);
        }
        return result;
    }

    async placeOrders() {
        const documentsToOrder = Object.entries(this.checkedDocuments)
            .filter(([, checked]) => checked)
            .map(([uuid]) => this.filteredDocuments.find((el) => el.doc.uuid === uuid))
            .filter(document => document != null);

        for (const document of documentsToOrder) {
            await this.confirmPlaceOrder(document);
        }
    }

    transferChanges(document: SalesDocument) {
        if (!this.documentsIntegrity[document.uuid]) {
            this.salesDocumentService.transferChanges(document);
        }
    }

    async setGeneralAccess() {
        this.hasAccessTo.createQuotation = await this.permissionsService.hasPermission([
            'sell-own-quotations',
            'sell-all-quotations-manage',
            'sell-resource-all-quotations-manage',
        ]);
        this.hasAccessTo.createServiceOrder = await this.permissionsService.hasPermission([
            'all-service-orders-manage',
        ]);
        this.hasAccessTo.placeOrder = await this.permissionsService.hasPermission([
            'purchase-make-order',
        ]);
        this.hasAccessTo.viewBuyPrices = await this.permissionsService.hasPermission([
            'purchase-prices-view',
        ]);
    }

    async setDocumentsAccess() {
        for (const document of this.filteredDocuments) {
            this.documentActionsAccessBeforeSynced.set(document.doc.uuid, await this.hasAccess(document));
        }
    }

    updateDocumentAccessAfterSync() {
        for (const document of this.filteredDocuments) {
            const accessBeforeSynced = {
                ...this.documentActionsAccessBeforeSynced.get(document.doc.uuid)
            };
            accessBeforeSynced.order = accessBeforeSynced.order && this.getDocumentSetStatus(document.doc.uuid) === Synced.Synced;
            accessBeforeSynced.transferChanges = accessBeforeSynced.transferChanges && this.getDocumentSetStatus(document.doc.uuid) === Synced.Synced;
            accessBeforeSynced.copy = accessBeforeSynced.copy && this.getDocumentSetStatus(document.doc.uuid) === Synced.Synced;
            this.documentActionsAccess.set(document.doc.uuid, accessBeforeSynced);
        }
    }

    updateDocumentAccessAfterDelete(documentId: string) {
        if (this.documentActionsAccess.has(documentId)) {
            const accessBeforeSynced = this.documentActionsAccessBeforeSynced.get(documentId);
            accessBeforeSynced.copy = false;
            accessBeforeSynced.delete = false;
            accessBeforeSynced.order = false;
            accessBeforeSynced.select = false;
            accessBeforeSynced.transferChanges = false;
            accessBeforeSynced.open = false;
            this.documentActionsAccess.set(documentId, accessBeforeSynced);
        }
    }

    showIntegrityWarning(document: SalesDocument) {
        this.toastService.add({
            title: this.translateService.instant('SALES_DOCUMENT|Niespójne dokumenty'),
            body: this.translateService.instant(
                'SALES_DOCUMENT|Dokumenty sprzedaży i zakupu nie są ze sobą spójne. Dokument zakupu został aneksowany. Zmiany nie zostały jeszcze przeniesione na dokument sprzedaży.'
            ),
            buttons: [
                {
                    title: this.translateService.instant('SALES_DOCUMENT|Przenieś zmiany'),
                    onClick: () => this.transferChanges(document),
                },
            ],
            severity: 'warn',
        });
    }

    async hasAccess(document: DocumentWithChildren) {
        if (this.deletedDocuments.has(document.doc.uuid)) {
            document.doc.isDeleted = true;
            return {
                select: false,
                copy: false,
                order: false,
                orderInvalid: false,
                transferChanges: false,
                delete: false,
                open: false,
            };
        }
        let canManage = false;

        if (document.doc.salesDocumentTypeId === SalesDocumentType.Order) {
            canManage = await this.salesDocumentService.hasAccessToOrder(document.doc, 'manage');
        } else if (document.doc.salesDocumentTypeId === SalesDocumentType.Inquiry) {
            canManage = await this.salesDocumentService.hasAccessToManageInquiry(document.doc);
        } else if (document.doc.salesDocumentTypeId === SalesDocumentType.ServiceOrder) {
            canManage = await this.salesDocumentService.hasAccessToServiceOrder(document.doc);
        } else {
            canManage = await this.salesDocumentService.hasAccessToManageQuotation(document.doc);
        }
        let orderChildren = true;
        let orderChildrenInvalid = false;

        for (const child of document.children) {
            const { invalidOffer: invalidChild } = this.offerValidityService.getValidDate(child);
            const canManageChildren = await this.salesDocumentService.hasAccessToOrder(
                child,
                'manage'
            );
            orderChildren =
                orderChildren &&
                canManageChildren &&
                child.isConfirmed &&
                !child.isCanceled &&
                !child.isOrderPlaced &&
                !child.isAnnexed &&
                !invalidChild &&
                (await this.permissionsService.hasPermission(['purchase-make-order'])) &&
                child.externalSource !== 'ERP' &&
                child.salesDocumentTypeId !== SalesDocumentType.Inquiry;
            orderChildrenInvalid = orderChildrenInvalid || invalidChild;
        }
        if (document.onlyPurchase || this.side === 'sales') {
            const { invalidOffer: invalidDocument } = this.offerValidityService.getValidDate(
                document.doc
            );
            orderChildren =
                canManage &&
                document.doc.isConfirmed &&
                !document.doc.isCanceled &&
                !document.doc.isOrderPlaced &&
                !document.doc.isAnnexed &&
                !invalidDocument &&
                (await this.permissionsService.hasPermission(['purchase-make-order'])) &&
                document.doc.externalSource !== 'ERP' &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.Inquiry;
            orderChildrenInvalid = orderChildrenInvalid || invalidDocument;
        }

        return {
            select:
                canManage &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                !document.doc.isConfirmed &&
                !document.doc.isCanceled &&
                !document.doc.isOrderPlaced &&
                !document.doc.isAnnexed &&
                document.doc.externalSource !== 'ERP',
            copy: canManage && document.doc.salesDocumentTypeId !== SalesDocumentType.Inquiry && !document.doc.isCanceled && this.documentsIntegrity[document.doc.uuid],
            order: orderChildren,
            orderInvalid: orderChildrenInvalid,
            transferChanges:
                canManage &&
                this.documentsIntegrity[document.doc.uuid] === false &&
                !document.doc.isCanceled &&
                document.doc.externalSource !== 'ERP' &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.Inquiry,
            delete:
                !document.doc.isOrderPlaced &&
                (document.doc.sellerCompanyId === document.doc.author.companyId ||
                this.configService.user.user.ActiveCompanyLicenseType === 'sublicense') &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.Order &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.Inquiry &&
                document.doc.salesDocumentTypeId !== SalesDocumentType.ServiceOrder &&
                document.doc.externalSource !== 'ERP' &&
                canManage,
            open: true,
        };
    }

    setListCrumbs() {
        const crumbs = [];

        if (this.isOrderList) {
            if (this.side === 'purchase') {
                if (this.onlyUnplacedOrders) {
                    crumbs.push({
                        label: this.translateService.instant(
                            'SALES_DOCUMENT|Lista zamówień u dostawcy do potwierdzenia'
                        ),
                        url: '/trade/offer/list/orders/unplaced',
                    });
                } else {
                    crumbs.push({
                        label: this.translateService.instant(
                            'SALES_DOCUMENT|Lista zamówień u dostawcy'
                        ),
                        url: '/trade/offer/list/orders/purchase',
                    });
                }
            } else {
                crumbs.push({
                    label: this.translateService.instant(
                        'SALES_DOCUMENT|Lista zamówień klientów końcowych'
                    ),
                    url: '/trade/offer/list/orders/sales',
                });
            }
        } else if (this.isInquiryList) {
            if (this.side === 'purchase') {
                crumbs.push({
                    label: this.translateService.instant(
                        'SALES_DOCUMENT|Lista zapytań ofertowych u dostawcy'
                    ),
                    url: '/trade/offer/list/inquiries/purchase',
                });
            } else {
                crumbs.push({
                    label: this.translateService.instant(
                        'SALES_DOCUMENT|Lista zapytań ofertowych klientów końcowych'
                    ),
                    url: '/trade/offer/list/inquiries/sales',
                });
            }
        } else if (this.isServiceOrderList) {
            crumbs.push({
                label: this.translateService.instant(
                    'SALES_DOCUMENT|Lista zamówień serwisowych'
                ),
                url: '/trade/offer/list/service-orders',
            });
        } else {
            crumbs.push({
                label: this.translateService.instant('SALES_DOCUMENT|Lista ofert'),
                url: '/trade/offer/list/offers',
            });
        }

        SalesDocumentsListComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        SalesDocumentsListComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
    }

    getOldestCreatedDate(documents: SalesDocument[]) {
        let oldestDate = new Date(documents[0].created);

        for (const document of documents) {
            if (new Date(document.created) < oldestDate) {
                oldestDate = new Date(document.created);
            }
        }
        return oldestDate;
    }

    getLatestModifiedDate(documents: SalesDocument[]) {
        let latestDate = new Date(documents[0].modified);

        for (const document of documents) {
            if (new Date(document.modified) > latestDate) {
                latestDate = new Date(document.modified);
            }
        }
        return latestDate;
    }

    getOldestRequestedDeadline(documents: SalesDocument[]) {
        let oldestDate = new Date(documents[0].requestedDeadline);

        for (const document of documents) {
            if (new Date(document.requestedDeadline) < oldestDate) {
                oldestDate = new Date(document.requestedDeadline);
            }
        }
        return oldestDate;
    }

    hasOneStatus(documents: SalesDocument[]) {
        const firstStatus = documents[0].statusId;

        for (const document of documents) {
            if (document.statusId !== firstStatus) {
                return false;
            }
        }
        return true;
    }

    hasOneSellerCompany(documents: SalesDocument[]) {
        const firstStatus = documents[0].sellerCompanyId;

        for (const document of documents) {
            if (document.sellerCompanyId !== firstStatus) {
                return false;
            }
        }
        return true;
    }

    getSeller(document: SalesDocument) {
        return this.personsService.getSeller(document);
    }

    getSellerCompany(document: SalesDocument) {
        return Number(this.configService.user.user.ActiveCompany) ===
            Number(document?.sellerCompanyId)
            ? this.configService.user.company
            : this.configService.user.accountManagerCompany;
    }

    getDepartments(documents: SalesDocument | SalesDocument[]) {
        const documentArray: SalesDocument[] = Array.isArray(documents) ? documents : [documents];
        const documentDepartments: string[] = [];

        for (const document of documentArray) {
            if (
                document.sellerProductDepartmentId &&
                this.departments[document.sellerProductDepartmentId]
            ) {
                const dept = this.departments[document.sellerProductDepartmentId];
                const departmentName: string = typeof dept === 'string' ? dept : dept.name;

                if (!documentDepartments.includes(departmentName)) {
                    documentDepartments.push(departmentName);
                }
            }
        }
        return documentDepartments.join(', ');
    }

    sum(documents: SalesDocument[], key: 'priceBeforeDiscountsValue' | 'priceAfterDiscountsValue') {
        let sum = 0;
        for (const document of documents) {
            sum += document[key];
        }
        return sum;
    }

    trackByUuid(index: number, item: DocumentWithChildren): string {
        return item.doc.uuid;
    }

    switchOfflineMode(offline: boolean) {
        this.offline = offline;
        this.updateDocuments(true);
        this.saveConfiguration();
    }

    updateDocumentsPairs(connections: SalesDocumentConnections) {
        for (const salesDocumentId in connections) {
            if ({}.hasOwnProperty.call(connections, salesDocumentId)) {
                this.buyDocumentsMap[salesDocumentId] = Object.keys(connections[salesDocumentId]).filter(el => el[el.length - 1] !== '^');
            }
        }
    }

    getDocumentSet(uuid: string) {
        if (!this.buyDocumentsMap[uuid]) {
            return [uuid];
        }
        return [uuid, ...this.buyDocumentsMap[uuid]];
    }

    getDocumentSetStatus(uuid: string) {
        return this.syncService.getSynced(
            this.getDocumentSet(uuid).filter((el) => el),
            this.synced,
            !this.offline
        );
    }

    confirmDelete(document: SalesDocument) {
        const title = this.translateService.instant('SALES_DOCUMENT|Potwierdzenie usunięcia');
        const body = this.translateService.instant(
            'SALES_DOCUMENT|Czy chcesz usunąć ten dokument?'
        );

        this.toastService.add({
            title,
            body,
            severity: 'question',
            sticky: true,
            buttons: [
                {
                    title: this.translateService.instant('INTERFACE|Tak'),
                    onClick: () => this.delete(document),
                },
                {
                    title: this.translateService.instant('INTERFACE|Nie'),
                    onClick: () => null,
                },
            ],
        });
    }

    async delete(document: SalesDocument) {
        this.syncService.addCurrentDocumentId(document.uuid);
        await this.syncService.waitForSynced([document.uuid]);
        await this.salesDocumentService.deleteDocumentsRemotely([document]);
        await this.updateDocuments(true);
    }

    updateDebugMode = () => {
        this.showDebugSection = (window as any).isDebugMode();
    }

    async populateConnectedDocumentsStatuses(
        matchingDocuments: DocumentWithChildren[],
        allDocuments: SalesDocument[]
    ): Promise<void> {
        // Create document maps for lookup
        const documentsMapByUuid: Record<string, DocumentWithChildren> = {};

        for (const docWithChildren of matchingDocuments) {
            documentsMapByUuid[docWithChildren.doc.uuid] = docWithChildren;
        }

        // Get all document UUIDs to check
        const allDocumentUuids = allDocuments.map(doc => doc.uuid);

        // Batch fetch all parent documents at once instead of one by one
        const parentDocumentsMap = await this.documentManagerService.getBuyDocumentsForAllDocuments(allDocumentUuids);

        // Process document connections - analogous to backend logic
        for (const doc of allDocuments) {
            // Get parent documents from the batch result
            const buyDocuments = parentDocumentsMap.get(doc.uuid) || [];

            for (const salesDocument of buyDocuments) {
                if (salesDocument?.uuid && documentsMapByUuid[salesDocument.uuid]) {
                    // This document is connected to a parent document
                    const parentDocWithChildren = documentsMapByUuid[salesDocument.uuid];

                    // Add to connectedDocumentIds (analogous to backend logic)
                    if (!parentDocWithChildren.connectedDocumentIds.includes(doc.uuid)) {
                        parentDocWithChildren.connectedDocumentIds.push(doc.uuid);
                    }

                    // Add to connectedDocumentsStatuses (analogous to backend logic)
                    if (doc.statusId && !parentDocWithChildren.connectedDocumentsStatuses.includes(doc.statusId)) {
                        parentDocWithChildren.connectedDocumentsStatuses.push(doc.statusId);
                    }
                }
            }
        }

        // Also populate this.buyDocumentsMap for consistency with online mode
        for (const docWithChildren of matchingDocuments) {
            if (docWithChildren.connectedDocumentIds.length > 0) {
                this.buyDocumentsMap[docWithChildren.doc.uuid] = [...docWithChildren.connectedDocumentIds];
            }
        }
    }
}
